import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Fade,
  Grow,
  Button,
  alpha
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  Person as PersonIcon,
  Security as SecurityIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from "@mui/icons-material";

import { useFileStore } from "../../app/zustandStore";
import { useFileQuery } from "../../app/apiHooks";
import { userServerAddress } from "../../common/MetaSetting";
import { User } from "../../app/types";
import { getStandardButtonStyles } from "../../styles";
import { bffAuthService } from "../../services/BffAuthService";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);

  const { t } = useTranslation();
  const isAuthenticated = bffAuthService.isAuthenticated;
  const currentFile = useFileStore((state) => state.currentFile);
  const { data: fileData, isLoading, error } = useFileQuery();
  
  console.log('Dashboard component loaded');
  console.log('Dashboard - isAuthenticated:', isAuthenticated);

  useEffect(() => {
    console.log('Dashboard组件认证状态:', { 
      isAuthenticated,
      timestamp: new Date().toISOString(),
      path: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash
    });
    console.log('Dashboard useEffect - isAuthenticated:', isAuthenticated);
    console.log('Dashboard useEffect - isLoading:', isLoading);
    console.log('Dashboard useEffect - fileData:', fileData);
    console.log('Dashboard useEffect - error:', error);
    console.log('Dashboard useEffect - currentFile:', currentFile);
    
    if (isAuthenticated) {
      console.log('Dashboard - 已认证，使用TanStack Query获取文件信息');
      
      // 如果已经有文件数据（来自store或query），立即停止加载
      if (currentFile || fileData) {
        console.log('Dashboard - 已有文件数据，停止加载');
        setLoading(false);
        return;
      }
      
      // 如果查询完成（无论成功或失败），停止加载
      if (!isLoading) {
        console.log('Dashboard - 查询完成，停止加载');
        setLoading(false);
        return;
      }
      
      // 如果查询出错，停止加载
      if (error) {
        console.log('Dashboard - 查询出错，停止加载');
        setLoading(false);
        return;
      }
      
      // 如果查询正在进行中，保持加载状态
      console.log('Dashboard - 查询进行中，保持加载状态');
    }
  }, [isAuthenticated, isLoading, fileData, error, currentFile]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      p: { xs: 0, sm: 0, md: 0 }, 
      // 使用标准Flex布局，子元素填充父元素空间
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'stretch',
      alignItems: 'flex-start'
    }}>
      {/* 欢迎标题 */}
      <Fade in={true} timeout={800}>
        <Box sx={{ 
          mb: { xs: 4, sm: 5, md: 6 },
          p: { xs: 3, sm: 4, md: 5 },
          borderRadius: '0px',
          background: 'linear-gradient(135deg, rgba(26, 41, 128, 0.1) 0%, rgba(38, 208, 206, 0.05) 100%)',
          border: '1px solid rgba(26, 41, 128, 0.2)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          flexGrow: 1,
          width: '100%'
        }}>
          {/* 顶部装饰线 - 使用flex布局 */}
          <Box sx={{
            height: '2px',
            background: 'linear-gradient(90deg, #1a2980, #26d0ce)',
            width: '100%',
            flexShrink: 0,
            mb: 2,
            flexGrow: 1,
          }} />
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ 
              fontWeight: 800,
              background: 'linear-gradient(45deg, #1a2980 30%, #26d0ce 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: { xs: '1.75rem', md: '2.125rem' },
              mb: 2
            }}>
              {t('file.welcomeBack', { nickName: (currentFile?.nickName || fileData?.nickName || (fileData as any)?.data?.nickName || currentFile?.username || fileData?.username || (fileData as any)?.data?.username) || t('file.info.notSet') })}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ 
              fontWeight: 400,
              opacity: 0.8,
              fontSize: { xs: '1rem', md: '1.25rem' }
            }}>
              {t('file.dashboard.description')}
            </Typography>
          </Box>
        </Box>
      </Fade>

      {/* 统计卡片 - 使用标准Flex布局 */}
      <Box sx={{ 
        mb: { xs: 4, sm: 5, md: 6 },
        // 标准Flex布局，确保卡片正确排列且不超出边界
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: { xs: 2, sm: 2.5, md: 3 }, // 调整间距为更精确的值
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box'
      }}>
        {[
          { 
            title: t('file.dashboard.cards.profile.title'), 
            value: '100%', 
            description: t('file.dashboard.cards.profile.description'),
            icon: <PersonIcon sx={{ fontSize: { xs: 32, sm: 32 }, color: 'primary.main' }} />,
            color: 'primary',
            status: 'complete',
            action: t('file.dashboard.cards.profile.action')
          },
          { 
            title: t('file.dashboard.cards.security.title'), 
            value: '85%', 
            description: t('file.dashboard.cards.security.description'),
            icon: <SecurityIcon sx={{ fontSize: { xs: 32, sm: 32 }, color: 'success.main' }} />,
            color: 'success',
            status: 'good',
            action: t('file.dashboard.cards.security.action')
          },
          { 
            title: t('file.dashboard.cards.sessions.title'), 
            value: '0', 
            description: t('file.dashboard.cards.sessions.description'),
            icon: <TrendingUpIcon sx={{ fontSize: { xs: 32, sm: 32 }, color: 'info.main' }} />,
            color: 'info',
            status: 'active',
            action: t('file.dashboard.cards.sessions.action')
          },
          { 
            title: t('file.dashboard.cards.notifications.title'), 
            value: '0', 
            description: t('file.dashboard.cards.notifications.description'),
            icon: <NotificationsIcon sx={{ fontSize: { xs: 32, sm: 32 }, color: 'warning.main' }} />,
            color: 'warning',
            status: 'trending',
            action: t('file.dashboard.cards.notifications.action')
          }
        ].map((item, index) => (
          <Box key={index} sx={{ 
            // 响应式宽度设置 - 使用更精确的计算确保一排显示4个卡片
            // 使用公式：calc((100% - 总间距) / 4)
            // 间距计算：sm: 2.5 * 8px = 20px, 3个间距 = 60px; md: 3 * 8px = 24px, 3个间距 = 72px
            width: { xs: '100%', sm: 'calc((100% - 60px) / 4)', md: 'calc((100% - 72px) / 4)' },
            minWidth: { xs: '100%', sm: '180px', md: '220px' },
            maxWidth: '100%',
            boxSizing: 'border-box'
          }}>
            <Grow in={true} timeout={(index + 1) * 200}>
              <Card 
                onMouseEnter={() => setHoveredCard(item.title)}
                onMouseLeave={() => setHoveredCard(null)}
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: `linear-gradient(135deg, ${alpha('#1a2980', 0.08)}, ${alpha('#26d0ce', 0.05)})`,
                  border: `1px solid ${alpha('#1a2980', 0.2)}`,
                  backdropFilter: 'blur(10px)',
                  overflow: 'hidden',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 20px ${alpha('#1a2980', 0.15)}`,
            border: `1px solid ${alpha('#1a2980', 0.4)}`
                  }
                }}
              >
                {/* 卡片顶部装饰线 - 使用flex布局 */}
                <Box sx={{
                  height: '3px',
                  background: 'linear-gradient(90deg, #1a2980, #26d0ce)',
                  width: '100%',
                  flexShrink: 0,
                  transform: hoveredCard === item.title ? 'scaleX(1)' : 'scaleX(0)',
                  transition: 'transform 0.3s ease',
                  transformOrigin: 'center'
                }} />
                <CardContent sx={{ 
                  flexGrow: 1, 
                  textAlign: 'center',
                  p: { xs: 2, sm: 3 },
                  width: '100%',
                  boxSizing: 'border-box',
                  position: 'relative',
                  zIndex: 1
                }}>
                  <Box sx={{ 
                    mb: 2,
                    p: 1,
                    borderRadius: '12px',
                    background: `linear-gradient(135deg, ${alpha('#1a2980', 0.2)}, ${alpha('#26d0ce', 0.2)})`,
                    border: `1px solid ${alpha('#1a2980', 0.3)}`,
                    transition: 'all 0.3s ease',
                    transform: hoveredCard === item.title ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </Box>
                  
                  <Typography variant="h4" component="div" sx={{ 
                    fontWeight: 800, 
                    mb: 1,
                    background: 'linear-gradient(135deg, #1a2980, #26d0ce)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' }
                  }}>
                    {item.value}
                  </Typography>
                  
                  <Typography variant="h6" component="div" sx={{ 
                    fontWeight: 600, 
                    mb: 1,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}>
                    {item.title}
                  </Typography>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ 
                    mb: 2, 
                    opacity: 0.8,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {item.description}
                  </Typography>
                  
                  <Button
                    size="small"
                    endIcon={<ArrowForwardIcon />}
                    sx={getStandardButtonStyles({ variant: 'outlined' })}
                  >
                    {item.action}
                  </Button>
                </CardContent>
              </Card>
            </Grow>
          </Box>
        ))}
      </Box>


    </Box>
  );
};

export const SecureFDashboard = () => {
  console.log('SecureFDashboard组件渲染:', {
    timestamp: new Date().toISOString(),
    path: window.location.pathname
  });
  
  return (
    <Dashboard />
  );
};

export default SecureFDashboard;
